import React, { useState, useMemo, useCallback } from "react";
import { Button, Container,} from "reactstrap";
import TableContainer from "../../../common/table/TableContainer.js";
import { useQuery, useQueryClient } from "react-query";
import { CheckOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, EyeOutlined, PlusCircleOutlined, QuestionOutlined } from "@ant-design/icons";
import { Avatar, Dropdown, Menu } from "antd";
import toast from "react-hot-toast";
import { deleteDeveloper, getBusinessByMarchandId } from "../services/developerApi";
import AddEditDevForm from "../component/addEditDevForm";
import CompleteCompanyDeveloper from "../../company/component/completeCompany";
import { getPaymentMethods } from "../../transactions/services/transactionApi.js";
import '../utils/style.css';
import DeleteModalOTP from "../../../common/delete/modalDeleteOtp.js";
import DetailBusiness from "../component/detail/detailDeveloper";
import load from "../../../assets/images/load-img.png";
import CardImageItem from "../../../common/image/cardImage.js";
import { FormatCurrency } from "../../../common/format/formatCurrency.js";
import FormatDate from "../../../common/format/formatDate.js";

const Developer = () => {
  
  const queryClient = useQueryClient();
  document.title = `Business | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;
  const [modalState, setModalState] = useState({ type: null, data: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const currentuser = JSON.parse(sessionStorage.getItem('authUser'));
  const merchandId = currentuser?.user?.id || currentuser?.user?.merchantId;
  const role = currentuser?.roles;
  const permissions = currentuser?.user?.permissions;
  const isAuthorized = role == 'COLLABORATOR' ? permissions?.includes('CREATE_BUSINESS') : true;

  const { data: developers, isLoading, isError, error} = useQuery(
    ["developers", merchandId], 
    () => getBusinessByMarchandId(merchandId),
    {
      enabled: !!merchandId,
    }
  );

  const { data, isLoading: isLoadingPaym, isError: isErrorPaym, error: errorPaym } = useQuery(
    "paymentList", getPaymentMethods
  );
  const paymentList = Array.isArray(data?.data) ? data.data : [];

  const openModal = (type, data = null) => {
    setModalState((prevState) => (prevState.type === type ? { type: null, data: null } : { type, data }));
  };

  const handleEdit = useCallback((developer) => {
    openModal("edit", developer);
  }, []);

  const handleDetail = useCallback((developer) => {
    openModal("detail", developer);
  }, []);

  const openDeleteConfirmModal = useCallback((developerId) => {
    const developerToDelete = developers.find(d => d.id === developerId);
    if (developerToDelete) {
      setModalState({
        type: "delete",
        data: {
          developerId,
          developerName: developerToDelete.businessName,
        },
      });
    }
  }, [developers]);

  const closeDeleteConfirmModal = () => {
    setModalState({ type: null, data: null });
  };

  const handleDelete = async (developerId) => {
    setIsDeleting(true);
    try {
      await deleteDeveloper(developerId);
      queryClient.invalidateQueries("developers");
      toast.success("Le business a été supprimé avec succès");
    } catch (error) {
      toast.error(`Erreur de suppression, merci de contacter le support`);
      console.error('error', error?.message || error)
    } finally {
      setIsDeleting(false);
      closeDeleteConfirmModal();
    }
  };

  const columns = useMemo(() => [
    {
      Header: "Business",
      accessor: "businessName",
      filterable: false,
      Cell: ({ row }) => {
        const { businessName, logo } = row.original;

        return (
          <div className="d-flex align-items-center">
            <Avatar size={32} style={{ marginRight: 10 }}>
              <CardImageItem fileName={logo} token={currentuser?.token} />
            </Avatar>
            <span>{businessName}</span>
          </div>
        );
      }
    },
    { Header: "Montant", accessor: "balance", 
      Cell: ({ row }) => {
        return <span className="">{FormatCurrency(row.original.balance, row.original.currency)}</span>
      },
      filterable: false },
    {
      Header: "Date",
      accessor: "requestDate",
      Cell: ({ value }) => <FormatDate date={value} />,
      filterable: false,
    },
    { Header: "Secteur", accessor: "sectorName", filterable: false },
    {
      Header: "Mode",
      accessor: "environmentMod",
      Cell: ({ value }) => {
        let backgroundColor = "";
        let textColor = "";
        let icon = null;
        let label = "";

        switch (value) {
          case "LIVE":
            backgroundColor = "#EBFFEA";
            textColor = "#349C32";
            icon = <CheckOutlined style={{ color: textColor, marginRight: 6 }} />;
            label = "Production";
            break;
          case "SANDBOX":
            backgroundColor = "#F6F6F6";
            textColor = "#FF9F00";
            icon = <img src={load}/>;
            label = "Sandbox";
            break;
          default:
            backgroundColor = "#FFF2F2";
            textColor = "#bd9393ff";
            icon = <QuestionOutlined style={{ color: textColor, marginRight: 6 }} />;
            label = "Inconnu";
            break;
        }

        return (
          <span
            style={{
              backgroundColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              fontWeight: 500,
              fontSize: 13,
              gap: "6px"
            }}
          >
            {icon} {label}
          </span>
        );
      },
      filterable: false,
    },   
    {
      Header: "Actions",
      Cell: ({ row }) => {
        const menu = (
          <Menu>
            <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleDetail(row.original)} >
              Détail
            </Menu.Item>
            <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(row.original)} >
              Modifier
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => openDeleteConfirmModal(row.original.id)} danger >
              Supprimer
            </Menu.Item>
          </Menu>
        );
    
        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <div className="action-menu-trigger">
              <EllipsisOutlined />
            </div>
          </Dropdown>
        );
      },
      filterable: false,
    },
  ], [handleEdit, openDeleteConfirmModal, handleDetail]);

  const HeaderTable = () => (
    <div className="d-flex flex-column align-items-start gap-3 w-100">
      <div className='title-page mb-2'>
        Business
      </div>
      <Button
        className="table-btn"
        onClick={() => openModal("edit", null)}
        disabled={!isAuthorized}
      >
        <PlusCircleOutlined /> {isAuthorized ? "Nouveau business" : "Pas autorisé pour créer un business"}
      </Button>
      <div className="liste-table mb-4 d-flex align-items-center gap-2">
        <h5 className="mb-0">Liste de mes business</h5>
        <span className="badge-count">{developers?.length || 0}</span>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <HeaderTable />
          <TableContainer
            columns={columns}
            data={developers || []}
            isGlobalSearch={true}
            isGlobalFilter={true}
            customPageSize={10}
            className="custom-header-css"
            divClass="table-responsive table-card mb-3"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            SearchPlaceholder="Recherche..."
            HeaderTable={HeaderTable}
            loading ={isLoading}
            isError={isError}
          />
          
          {modalState.type === "edit" && (
            <AddEditDevForm
              modal={modalState.type === "edit"}
              setModal={setModalState}
              toggle={() => openModal("edit", modalState.data)}
              selectedDeveloper={modalState.data}
              currentuser={currentuser}
              paymentList={paymentList}
              isLoadingPaym = {isLoadingPaym}
              isErrorPaym = {isErrorPaym}
              errorPaym = {errorPaym}
            />
          )}
          {modalState.type === "detail" && (
            <DetailBusiness
              modal={modalState.type === "detail"}
              setModal={setModalState}
              toggle={() => openModal("detail", modalState.data)}
              selectedDeveloper={modalState.data}
              onUpdateDeveloper={(updatedDev) =>
                setModalState(prev => ({ ...prev, data: updatedDev }))
              }
              currentuser={currentuser}
            />
          )}
          {modalState.type === "complete" && (
            <CompleteCompanyDeveloper
              modal={modalState.type === "complete"}
              setModal={setModalState}
              toggle={() => openModal("complete", modalState.data)}
              selectedCompany={modalState.data}
              currentuser={currentuser}
            />
          )}

          {modalState.type === "delete" && (
            <DeleteModalOTP
              isOpen={true}
              title={`du business ${modalState.data.developerName}`}
              loading={isDeleting}
              onCancel={closeDeleteConfirmModal}
              onConfirm={() => handleDelete(modalState.data.developerId)}
            />
          )}

        </Container>
      </div>
    </React.Fragment>
  );
};

export default Developer;
